import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, filter, switchMap, take, Subject, map } from 'rxjs';
import { environment } from '@environments/environment';
import { Apk, ApkForm } from '@core/models';
import {
  ResponseSuccess,
  ResponseObjectSuccess,
} from '@interfaces/response-success';
import { Store } from '@ngrx/store';
import { selectCredential, selectApkList } from '@store/selectors';
import { Credential } from '@core/models';
import { transformApkUpsert } from '@core/helpers';
import { objectToFormData } from '@core/helpers/formdata';

@Injectable({ providedIn: 'root' })
export class ApkService {
  credential$!: Observable<Credential | null>;
  apkList$: Observable<Apk[] | null>;

  private createCompletedSource = new Subject<void>();
  createCompleted$ = this.createCompletedSource.asObservable();

  private updateCompletedSource = new Subject<void>();
  updateCompleted$ = this.updateCompletedSource.asObservable();

  private removeCompletedSource = new Subject<void>();
  removeCompleted$ = this.removeCompletedSource.asObservable();

  private closeDialogSource = new Subject<void>();
  closeDialog$ = this.closeDialogSource.asObservable();

  constructor(
    private http: HttpClient,
    private store: Store
  ) {
    this.credential$ = this.store.select(selectCredential);
    this.apkList$ = this.store.select(selectApkList);
  }

  createCompleted(): void {
    this.createCompletedSource.next();
    this.closeDialog();
  }

  updateCompleted(): void {
    this.updateCompletedSource.next();
    this.closeDialog();
  }

  removeCompleted(): void {
    this.removeCompletedSource.next();
    this.closeDialog();
  }

  closeDialog(): void {
    this.closeDialogSource.next();
  }

  find(id: string): Observable<ResponseSuccess<Apk>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseSuccess<Apk>>(
          `${environment.microConfiguraciones}/api/v1/version-manager/${id}`,
          { headers: { Authorization: token } }
        );
      }),
      take(1)
    );
  }

  findAll(moduleType?: string | null): Observable<ResponseSuccess<Apk>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        const params = {
          ...(!!moduleType && { moduleType }),
        };

        return this.http.get<ResponseSuccess<Apk>>(
          `${environment.microConfiguraciones}/api/v1/version-manager`,
          {
            headers: { Authorization: token },
            params: {
              ...params,
            }
          }
        );
      }),
      take(1)
    );
  }

  create(apkForm: ApkForm): Observable<ResponseObjectSuccess<Apk>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.post<ResponseObjectSuccess<Apk>>(
          `${environment.microConfiguraciones}/api/v1/version-manager/upload`,
          objectToFormData(transformApkUpsert({
            ...apkForm,
          })),
          { headers: { Authorization: token } }
        );
      }),
      take(1)
    );
  }

  update(
    id: string,
    apkForm: ApkForm
  ): Observable<ResponseObjectSuccess<Apk>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.patch<ResponseObjectSuccess<Apk>>(
          `${environment.microConfiguraciones}/api/v1/version-manager/${id}`,
          transformApkUpsert({
            ...apkForm,
          }),
          { headers: { Authorization: token } }
        );
      }),
      take(1)
    );
  }

  remove(id: string): Observable<ResponseObjectSuccess<Response>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.delete<ResponseObjectSuccess<Response>>(
          `${environment.microConfiguraciones}/api/v1/version-manager/${id}`,
          { headers: { Authorization: token } }
        );
      }),
      take(1)
    );
  }

  filter(text: string): Observable<Apk[]> {
    return this.apkList$.pipe(
      filter((apkList): apkList is Apk[] => apkList !== null && apkList.length > 0),
      map((apkList) => apkList.filter((apk) => apk.name.toLowerCase().includes(text.toLowerCase()))),
      take(1),
    );
  }
  // getDropdown(): Observable<MenuItem[]> {
  //   return this.apkList$.pipe(
  //     filter((apks): apks is Apk[] => !!apks),
  //     map((apks) => transformDropdown(apks, 'id', 'name')),
  //   );
  // }
}
